import { z } from 'zod';
import { baseMessageSchema, createMessageId } from './_base';

export enum UserEventMessageType {
  USEREVENT_SEND = 'USEREVENT_SEND',
  USEREVENT_ACK = 'USEREVENT_ACK',
  USEREVENT_ERROR = 'USEREVENT_ERROR',
  USEREVENT_FETCH = 'USEREVENT_FETCH',
  USEREVENT_FETCH_RESPONSE = 'USEREVENT_FETCH_RESPONSE',
  USEREVENT_SERVER_PUSH = 'USEREVENT_SERVER_PUSH',
}

// Define event types
export enum UserEventType {
  BETA_FLAG_OPT_IN = 'BETA_FLAG_OPT_IN',
  BETA_FLAG_OPT_OUT = 'BETA_FLAG_OPT_OUT',
  MAILING_LIST_SUBSCRIBE = 'MAILING_LIST_SUBSCRIBE',
  MAILING_LIST_UNSUBSCRIBE = 'MAILING_LIST_UNSUBSCRIBE',
  USER_METRICS_REQUEST = 'USER_METRICS_REQUEST',
  USER_PREFERENCE_UPDATE = 'USER_PREFERENCE_UPDATE',
  USER_ANALYTICS_EVENT = 'USER_ANALYTICS_EVENT',
  USER_SESSION_TRACKING = 'USER_SESSION_TRACKING',
  USER_OWNED_LOBBIES = 'USER_OWNED_LOBBIES',
}

// Common schemas for event data
const betaFlagEventDataSchema = z.object({
  flagId: z.string(),
  flagName: z.string().optional(),
  metadata: z.record(z.unknown()).optional(),
});

const mailingListEventDataSchema = z.object({
  listId: z.string(),
  listName: z.string().optional(),
  email: z.string().email().optional(),
  preferences: z.record(z.boolean()).optional(),
});

const userMetricsRequestDataSchema = z.object({
  metricTypes: z.array(z.string()),
  timeRange: z
    .object({
      start: z.number().optional(),
      end: z.number().optional(),
    })
    .optional(),
  filters: z.record(z.unknown()).optional(),
});

const userPreferenceUpdateDataSchema = z.object({
  preferenceKey: z.string(),
  preferenceValue: z.unknown(),
  category: z.string().optional(),
});

const userAnalyticsEventDataSchema = z.object({
  eventName: z.string(),
  properties: z.record(z.unknown()).optional(),
  userProperties: z.record(z.unknown()).optional(),
  sessionId: z.string().optional(),
});

const sessionTypeSchema = z.enum(['player', 'profile']);

export type SessionType = z.infer<typeof sessionTypeSchema>;

const playerSessionSchema = z.object({
  websocketId: z.string(),
  sessionType: z.literal(sessionTypeSchema.Enum.player),
  invitationId: z.string().optional(),
  timestamp: z.number(),
});

export type PlayerSession = z.infer<typeof playerSessionSchema>;

const profileSessionSchema = z.object({
  websocketId: z.string(),
  sessionType: z.literal(sessionTypeSchema.Enum.profile),
  invitationId: z.string().optional(),
  timestamp: z.number(),
});

export type ProfileSession = z.infer<typeof profileSessionSchema>;

const userSessionTrackingDataSchema = z.object({
  playerSessions: z.array(playerSessionSchema),
  profileSessions: z.array(profileSessionSchema),
  currentLobby: z.string().optional(),
  lastPlayerWebsocketId: z.string().optional(),
});

const userOwnedLobbiesDataSchema = z.object({
  ownedLobbies: z.array(z.string()), // Array of invitation IDs
});

const userEventDataSchema = z.union([
  betaFlagEventDataSchema,
  mailingListEventDataSchema,
  userMetricsRequestDataSchema,
  userPreferenceUpdateDataSchema,
  userAnalyticsEventDataSchema,
  userSessionTrackingDataSchema,
  userOwnedLobbiesDataSchema,
]);

// Stored user event schema for fetch responses
const storedUserEventSchema = z.object({
  id: z.string(),
  userId: z.string(),
  eventType: z.nativeEnum(UserEventType),
  eventData: userEventDataSchema,
  createdAt: z.number(),
  updatedAt: z.number().optional(),
  metadata: z.record(z.unknown()).optional(),
});

// Payload schemas
const userEventSendPayloadSchema = z.object({
  eventType: z.nativeEnum(UserEventType),
  eventData: userEventDataSchema,
  correlationId: z.string().optional(),
});

const userEventAckPayloadSchema = z.object({
  success: z.boolean(),
  correlationId: z.string().optional(),
  message: z.string().optional(),
});

const userEventErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.unknown()).optional(),
  }),
  correlationId: z.string().optional(),
  eventType: z.nativeEnum(UserEventType).optional(),
});

const userEventFetchPayloadSchema = z.object({
  eventTypes: z.array(z.nativeEnum(UserEventType)).optional(), // Filter by event types
  limit: z.number().min(1).max(100).default(50).optional(),
  offset: z.number().min(0).default(0).optional(),
  timeRange: z
    .object({
      start: z.number().optional(),
      end: z.number().optional(),
    })
    .optional(),
  correlationId: z.string().optional(),
});

const userEventFetchResponsePayloadSchema = z.object({
  success: z.boolean(),
  events: z.array(storedUserEventSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
  correlationId: z.string().optional(),
});

const userEventServerPushPayloadSchema = z.object({
  eventType: z.nativeEnum(UserEventType),
  eventData: userEventDataSchema,
});

// Full message schemas
const userEventSendSchema = baseMessageSchema
  .extend({
    type: z.literal(UserEventMessageType.USEREVENT_SEND),
  })
  .merge(userEventSendPayloadSchema);

const userEventAckSchema = baseMessageSchema
  .extend({
    type: z.literal(UserEventMessageType.USEREVENT_ACK),
  })
  .merge(userEventAckPayloadSchema);

const userEventErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(UserEventMessageType.USEREVENT_ERROR),
  })
  .merge(userEventErrorPayloadSchema);

const userEventFetchSchema = baseMessageSchema
  .extend({
    type: z.literal(UserEventMessageType.USEREVENT_FETCH),
  })
  .merge(userEventFetchPayloadSchema);

const userEventFetchResponseSchema = baseMessageSchema
  .extend({
    type: z.literal(UserEventMessageType.USEREVENT_FETCH_RESPONSE),
  })
  .merge(userEventFetchResponsePayloadSchema);

const userEventServerPushSchema = baseMessageSchema
  .extend({
    type: z.literal(UserEventMessageType.USEREVENT_SERVER_PUSH),
  })
  .merge(userEventServerPushPayloadSchema);

export const userEventMessageSchema = z.discriminatedUnion('type', [
  userEventSendSchema,
  userEventAckSchema,
  userEventErrorSchema,
  userEventFetchSchema,
  userEventFetchResponseSchema,
  userEventServerPushSchema,
]);

// Payload types
export type UserEventSendPayload = z.infer<typeof userEventSendPayloadSchema>;
export type UserEventAckPayload = z.infer<typeof userEventAckPayloadSchema>;
export type UserEventErrorPayload = z.infer<typeof userEventErrorPayloadSchema>;
export type UserEventFetchPayload = z.infer<typeof userEventFetchPayloadSchema>;
export type UserEventFetchResponsePayload = z.infer<typeof userEventFetchResponsePayloadSchema>;
export type UserEventServerPushPayload = z.infer<typeof userEventServerPushPayloadSchema>;

// Event data types for convenience
export type BetaFlagEventData = z.infer<typeof betaFlagEventDataSchema>;
export type MailingListEventData = z.infer<typeof mailingListEventDataSchema>;
export type UserMetricsRequestData = z.infer<typeof userMetricsRequestDataSchema>;
export type UserPreferenceUpdateData = z.infer<typeof userPreferenceUpdateDataSchema>;
export type UserAnalyticsEventData = z.infer<typeof userAnalyticsEventDataSchema>;
export type UserSessionTrackingData = z.infer<typeof userSessionTrackingDataSchema>;
export type UserOwnedLobbiesData = z.infer<typeof userOwnedLobbiesDataSchema>;
export type UserEventData = z.infer<typeof userEventDataSchema>;
export type StoredUserEvent = z.infer<typeof storedUserEventSchema>;

// Full message type
export type UserEventMessage = z.infer<typeof userEventMessageSchema>;

type PayloadType<T extends UserEventMessageType> = T extends UserEventMessageType.USEREVENT_SEND
  ? UserEventSendPayload
  : T extends UserEventMessageType.USEREVENT_ACK
    ? UserEventAckPayload
    : T extends UserEventMessageType.USEREVENT_ERROR
      ? UserEventErrorPayload
      : T extends UserEventMessageType.USEREVENT_FETCH
        ? UserEventFetchPayload
        : T extends UserEventMessageType.USEREVENT_FETCH_RESPONSE
          ? UserEventFetchResponsePayload
          : T extends UserEventMessageType.USEREVENT_SERVER_PUSH
            ? UserEventServerPushPayload
            : never;

export function generateUserEventMessage<T extends UserEventMessageType>(type: T, payload: PayloadType<T>): UserEventMessage {
  const base = {
    id: createMessageId(),
    timestamp: Date.now(),
    type,
  };

  switch (type) {
    case UserEventMessageType.USEREVENT_SEND:
      return userEventSendSchema.parse({
        ...base,
        ...payload,
      });
    case UserEventMessageType.USEREVENT_ACK:
      return userEventAckSchema.parse({
        ...base,
        ...payload,
      });
    case UserEventMessageType.USEREVENT_ERROR:
      return userEventErrorSchema.parse({
        ...base,
        ...payload,
      });
    case UserEventMessageType.USEREVENT_FETCH:
      return userEventFetchSchema.parse({
        ...base,
        ...payload,
      });
    case UserEventMessageType.USEREVENT_FETCH_RESPONSE:
      return userEventFetchResponseSchema.parse({
        ...base,
        ...payload,
      });
    case UserEventMessageType.USEREVENT_SERVER_PUSH:
      return userEventServerPushSchema.parse({
        ...base,
        ...payload,
      });
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled message type: ${exhaustiveCheck}`);
    }
  }
}
